/**
 * PassportVisaScene.jsx
 *
 * Cinematic centerpiece that replaces the previous airplane animation.
 *
 * The choreography (16-second loop, eased throughout):
 *   1. A closed passport gently floats on screen.
 *   2. Its cover swings open like a real travel-document.
 *   3. A Schengen-style visa sticker drops in and settles onto the right page.
 *   4. An official ink stamp falls from above, "stamps" the visa
 *      (the visa briefly pulses) and then settles next to the visa.
 *   5. The whole spread holds for a beat, fades, and resets for the next loop.
 *
 * Implementation notes:
 *   - All transforms are pure CSS keyframes for smoothness.
 *   - Each layer animates independently so the timing reads naturally.
 *   - `prefers-reduced-motion` collapses to a static settled state.
 */
export default function PassportVisaScene({ theme }) {
  const isDark = theme.name === "dark";
  const gold = theme.gold;
  const inkRed = "#a4231b";
  const paper = isDark ? "#f7eecf" : "#ffffff";
  const paperEdge = isDark ? "#c9b787" : "#d8c98f";
  const coverBg = isDark ? "#3d2c12" : "#5a3b14";
  const coverInk = "#e6c777";
  const stampInk = inkRed;
  const visaBg = isDark ? "#e6dabb" : "#f3e9c8";
  const visaBlue = "#1f3a6b";
  const glow = isDark ? "rgba(212,175,55,0.35)" : "rgba(166,124,26,0.30)";

  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        top: "30px",
        left: "50%",
        transform: "translateX(-50%)",
        width: "min(380px, 84vw)",
        height: "240px",
        pointerEvents: "none",
        zIndex: 0,
        opacity: isDark ? 0.65 : 0.85,
        filter: `drop-shadow(0 12px 28px ${glow})`,
      }}
      data-testid="passport-visa-scene"
    >
      <div className="vta-scene">
        {/* Open passport spread (two pages) — base layer */}
        <svg className="vta-spread" viewBox="0 0 420 240" width="100%" height="100%">
          <defs>
            <linearGradient id="vta-paper-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={paper} stopOpacity="1" />
              <stop offset="100%" stopColor={paper} stopOpacity="0.82" />
            </linearGradient>
          </defs>
          {/* Left page (inside cover side) */}
          <path
            d="M 50 30 L 210 30 L 210 210 L 60 210 Q 50 210 50 200 Z"
            fill="url(#vta-paper-grad)" stroke={paperEdge} strokeWidth="1.1"
          />
          {/* Right page */}
          <path
            d="M 210 30 L 370 30 Q 380 30 380 40 L 380 210 L 210 210 Z"
            fill="url(#vta-paper-grad)" stroke={paperEdge} strokeWidth="1.1"
          />
          {/* Center spine */}
          <line x1="210" y1="30" x2="210" y2="210" stroke={paperEdge}
            strokeWidth="1.1" strokeDasharray="2 3" />

          {/* Left page content — header bar + lines */}
          <rect x="68" y="50" width="124" height="3" fill={gold} opacity="0.85" />
          <line x1="68"  y1="68"  x2="190" y2="68"  stroke={paperEdge} strokeWidth="1" />
          <line x1="68"  y1="80"  x2="180" y2="80"  stroke={paperEdge} strokeWidth="0.9" />
          <line x1="68"  y1="92"  x2="190" y2="92"  stroke={paperEdge} strokeWidth="0.9" />
          {/* Round stamp ghost on left page */}
          <g opacity="0.5">
            <circle cx="120" cy="150" r="28" stroke={inkRed} strokeWidth="1.4"
              strokeDasharray="3 2" fill="none" />
            <circle cx="120" cy="150" r="18" stroke={inkRed} strokeWidth="1" fill="none" />
            <line x1="100" y1="150" x2="140" y2="150" stroke={inkRed} strokeWidth="1.2" />
          </g>
        </svg>

        {/* Passport cover — swings open from the center spine */}
        <svg
          className="vta-cover"
          viewBox="0 0 200 220"
          width="200" height="220"
        >
          {/* Cover face */}
          <rect x="6" y="14" width="188" height="200" rx="8"
            fill={coverBg} stroke={coverInk} strokeWidth="1.4" />
          {/* Outer rule frame */}
          <rect x="16" y="24" width="168" height="180" rx="4"
            fill="none" stroke={coverInk} strokeWidth="0.8" opacity="0.5" />
          {/* Title bar */}
          <line x1="40" y1="56" x2="160" y2="56" stroke={coverInk} strokeWidth="2.2" />
          <line x1="58" y1="68" x2="142" y2="68" stroke={coverInk} strokeWidth="1.1" />
          {/* Emblem — palm + crossed swords (subtle) */}
          <g transform="translate(100 120)" stroke={coverInk}
             strokeWidth="1.4" fill="none" strokeLinecap="round">
            <line x1="0" y1="6" x2="0" y2="-12" />
            <path d="M 0 -12 q -8 -3 -10 -8 M 0 -12 q 8 -3 10 -8 M 0 -12 q -5 -5 -5 -10 M 0 -12 q 5 -5 5 -10" />
            <line x1="-16" y1="14" x2="16" y2="2" />
            <line x1="16" y1="14" x2="-16" y2="2" />
          </g>
          {/* Bottom country label bar */}
          <line x1="50" y1="170" x2="150" y2="170" stroke={coverInk}
            strokeWidth="1.4" opacity="0.85" />
        </svg>

        {/* Schengen-style visa sticker — drops in onto the right page */}
        <svg
          className="vta-visa"
          viewBox="0 0 160 100"
          width="160" height="100"
        >
          {/* Sticker body */}
          <rect x="2" y="2" width="156" height="96" rx="4"
            fill={visaBg} stroke={visaBlue} strokeWidth="1.4" />
          {/* "SCHENGEN" header strip */}
          <rect x="10" y="8" width="80" height="10" fill={visaBlue} opacity="0.9" />
          <text x="14" y="16" fontSize="7" fontWeight="700"
            fill="#fff" fontFamily="Arial, sans-serif" letterSpacing="1">
            SCHENGEN
          </text>
          {/* EU stars circle (12 stars) */}
          {Array.from({ length: 12 }, (_, i) => {
            const a = (i * Math.PI * 2) / 12 - Math.PI / 2;
            const cx = 130 + Math.cos(a) * 13;
            const cy = 18 + Math.sin(a) * 13;
            return <circle key={i} cx={cx} cy={cy} r="1.4" fill="#f5c518" />;
          })}
          <circle cx="130" cy="18" r="15" stroke={visaBlue}
            strokeWidth="0.9" fill="none" strokeDasharray="0.6 0.6" />
          {/* Photo slot */}
          <rect x="10" y="28" width="36" height="48" rx="2"
            fill="#fff" stroke={visaBlue} strokeWidth="1" />
          <circle cx="28" cy="44" r="6" stroke={visaBlue} strokeWidth="1" fill="none" />
          <path d="M16 70 Q 28 58 40 70" stroke={visaBlue} strokeWidth="1" fill="none" />
          {/* Data lines */}
          <line x1="52" y1="32" x2="148" y2="32" stroke={visaBlue} strokeWidth="0.9" />
          <line x1="52" y1="40" x2="138" y2="40" stroke={visaBlue} strokeWidth="0.7" />
          <line x1="52" y1="48" x2="148" y2="48" stroke={visaBlue} strokeWidth="0.7" />
          <line x1="52" y1="56" x2="130" y2="56" stroke={visaBlue} strokeWidth="0.7" />
          {/* MRZ */}
          <g stroke={visaBlue} strokeWidth="0.7">
            {Array.from({ length: 26 }, (_, i) => (
              <line key={`m1-${i}`} x1={6 + i * 5.7} y1="84" x2={9.4 + i * 5.7} y2="84" />
            ))}
            {Array.from({ length: 26 }, (_, i) => (
              <line key={`m2-${i}`} x1={6 + i * 5.7} y1="92" x2={9.4 + i * 5.7} y2="92" />
            ))}
          </g>
        </svg>

        {/* Official approval stamp — drops, impacts, settles */}
        <svg
          className="vta-stamp"
          viewBox="0 0 110 110"
          width="110" height="110"
        >
          <circle cx="55" cy="55" r="48" stroke={stampInk}
            strokeWidth="3" strokeDasharray="4 3" fill="none" />
          <circle cx="55" cy="55" r="36" stroke={stampInk}
            strokeWidth="2.2" fill="none" />
          <text x="55" y="52" textAnchor="middle" fontSize="13" fontWeight="900"
            fill={stampInk} fontFamily="Arial, sans-serif" letterSpacing="2">
            APPROVED
          </text>
          <line x1="32" y1="62" x2="78" y2="62" stroke={stampInk}
            strokeWidth="1.8" />
          <line x1="38" y1="70" x2="72" y2="70" stroke={stampInk}
            strokeWidth="1.2" strokeDasharray="1.5 1.5" />
        </svg>
      </div>

      <style>{`
        .vta-scene {
          position: relative;
          width: 100%;
          height: 100%;
        }

        /* The spread floats gently, full-width base */
        .vta-spread {
          position: absolute;
          top: 0; left: 0;
          width: 100%;
          height: 100%;
          animation: vta-spread-float 16s ease-in-out infinite;
          transform-origin: center;
        }
        @keyframes vta-spread-float {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-6px); }
        }

        /* Cover sits on the right half before opening, rotates from center spine */
        .vta-cover {
          position: absolute;
          top: 14px;
          left: 50%;
          width: 47%;
          height: auto;
          transform-origin: left center;
          animation: vta-cover-open 16s cubic-bezier(0.6, 0.1, 0.2, 1) infinite;
          will-change: transform;
        }
        @keyframes vta-cover-open {
          0%, 8%    { transform: rotateY(0deg); }
          22%       { transform: rotateY(-100deg); }
          30%, 88%  { transform: rotateY(-158deg); }
          96%, 100% { transform: rotateY(0deg); }
        }

        /* Visa drops in from above and lands onto the right page */
        .vta-visa {
          position: absolute;
          top: 60px;
          left: 56%;
          width: 36%;
          height: auto;
          transform-origin: center;
          opacity: 0;
          animation: vta-visa-land 16s cubic-bezier(0.4, 0, 0.2, 1) infinite;
          will-change: transform, opacity;
        }
        @keyframes vta-visa-land {
          0%, 32%   { transform: translate(0, -260px) rotate(-8deg); opacity: 0; }
          38%       { transform: translate(0, -260px) rotate(-8deg); opacity: 1; }
          50%       { transform: translate(0, 0) rotate(-3deg); opacity: 1; }
          /* Tiny bounce when the stamp hits */
          62%       { transform: translate(0, 0) rotate(-3deg) scale(1.04); }
          66%       { transform: translate(0, 0) rotate(-3deg) scale(1); }
          88%       { transform: translate(0, 0) rotate(-3deg); opacity: 1; }
          96%, 100% { transform: translate(0, -10px) rotate(-3deg); opacity: 0; }
        }

        /* Stamp drops, impacts, then settles slightly off the visa */
        .vta-stamp {
          position: absolute;
          top: 70px;
          left: 62%;
          width: 24%;
          height: auto;
          transform-origin: center;
          opacity: 0;
          animation: vta-stamp-drop 16s cubic-bezier(0.55, 0, 0.2, 1) infinite;
          will-change: transform, opacity;
        }
        @keyframes vta-stamp-drop {
          0%, 52%   { transform: translate(0, -200px) scale(1.6) rotate(-12deg); opacity: 0; }
          55%       { transform: translate(0, -200px) scale(1.6) rotate(-12deg); opacity: 1; }
          62%       { transform: translate(0, 0) scale(1) rotate(-10deg); opacity: 1; }
          /* recoil after impact */
          66%       { transform: translate(0, -8px) scale(1.03) rotate(-10deg); opacity: 1; }
          72%       { transform: translate(0, 0) scale(1) rotate(-10deg); opacity: 0.95; }
          88%       { transform: translate(0, 0) scale(1) rotate(-10deg); opacity: 0.9; }
          96%, 100% { transform: translate(0, -10px) scale(1) rotate(-10deg); opacity: 0; }
        }

        @media (prefers-reduced-motion: reduce) {
          .vta-cover { transform: rotateY(-158deg); animation: none; }
          .vta-visa  { transform: none; opacity: 1; animation: none; }
          .vta-stamp { transform: none; opacity: 0.95; animation: none; }
          .vta-spread { animation: none; }
        }
      `}</style>
    </div>
  );
}
